import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { type AirActionTrackingLocation } from '@air/analytics';
import { useAirModal } from '@air/provider-modal';
import { resetSelectedItemsAction, selectedItemsCountSelector, selectedItemsSelector } from '@air/redux-selected-items';

import { DeleteCanvasConfirmationModal } from '~/components/Canvas/DeleteCanvasConfirmationModal';
import { GalleryItemType } from '~/components/Gallery/types';
import { DeleteAssetsAndBoardsModal } from '~/components/Modals/DeleteAssetsAndBoards';
import { useDeleteBoardsModal } from '~/components/Modals/DeleteBoards/useDeleteBoardsModal';
import { DeletePrivateClipsModal } from '~/components/Modals/DeletePrivateClipsModal';
import { useWorkspaceRightPanelStore } from '~/components/v2/store/features/workspace/useWorkspaceRightPanelStore';
import { useBoardPermissionsCache } from '~/hooks/useBoardPermissionsCache';
import { useAccountContext } from '~/providers/AccountProvider';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { CentralizedBoardSelectors } from '~/store/centralizedBoard/selectors';
import {
  hasOnlyBoardsSelectedSelector,
  hasOnlyCanvasesSelectedSelector,
  hasOnlyClipsSelectedSelector,
  selectedBoardsIdsSelector,
  selectedBoardsSelector,
  selectedClipsIdsInfoSelector,
} from '~/store/selectedItems/selectors';
import { type SelectableGalleryCanvasItem } from '~/store/selectedItems/types';
import { useGetBoardContainerPermissions } from '~/swr-hooks/boards/useGetBoardContainerPermissions';
import { canDeleteAsset } from '~/utils/permissions/assetPermissions';
import { canDeleteBoard, canDeleteCanvasInBoard } from '~/utils/permissions/boardPermissions';
import { canDeleteCanvasFromWorkspace, canManageCanvasesInWorkspace } from '~/utils/permissions/workspacePermissions';
import { useAirStore } from '~/utils/ReduxUtils';

export const useHandleDeletePrivateSelection = () => {
  const dispatch = useDispatch();
  const [showDeletePrivateClipsModal] = useAirModal(DeletePrivateClipsModal);
  const { showDeleteBoardsModal } = useDeleteBoardsModal();
  const [showDeleteAssetsAndBoardsModal] = useAirModal(DeleteAssetsAndBoardsModal);
  const [showDeleteCanvasConfirmationModal] = useAirModal(DeleteCanvasConfirmationModal);
  const { getBoardPermissions } = useBoardPermissionsCache();
  const { getBoardContainerPermissions } = useGetBoardContainerPermissions();
  const { data: account } = useAccountContext();
  const { data: workspacePermissions } = useCurrentWorkspacePermissionsContext();
  const store = useAirStore();

  const handleDelete = useCallback(
    (trackLocation: AirActionTrackingLocation) => {
      // Don't trigger the gallery delete modal when a canvas editor is open —
      // ReactFlow handles its own Delete/Backspace for canvas nodes.
      if (useWorkspaceRightPanelStore.getState().selectedCanvasId) {
        return;
      }

      // Canvas-only selection: show the canvas delete modal directly.
      if (hasOnlyCanvasesSelectedSelector(store.getState())) {
        const canvasItems = selectedItemsSelector(store.getState()).filter(
          (item): item is SelectableGalleryCanvasItem => item.type === GalleryItemType.canvas,
        );
        const canvasIds = canvasItems.map((item) => item.item.id);

        const currentBoardId = CentralizedBoardSelectors.boardId(store.getState());
        const currentBoardPermissions = currentBoardId ? getBoardPermissions(currentBoardId) : undefined;
        const canManageCanvases = canManageCanvasesInWorkspace(workspacePermissions);
        const canDeleteAllCanvases = canvasItems.every((canvasItem) => {
          const isOwner = !!account?.id && canvasItem.item.createdBy === account.id;

          return currentBoardId
            ? canDeleteCanvasInBoard(currentBoardPermissions) && (canManageCanvases || isOwner)
            : canDeleteCanvasFromWorkspace(workspacePermissions) && (canManageCanvases || isOwner);
        });

        if (!canDeleteAllCanvases) {
          return;
        }

        showDeleteCanvasConfirmationModal({
          canvasIds,
          boardId: currentBoardId,
          onDelete: () => dispatch(resetSelectedItemsAction()),
        });
        return;
      }

      const currentBoardId = CentralizedBoardSelectors.boardId(store.getState());
      const currentBoardPermissions = currentBoardId ? getBoardPermissions(currentBoardId) : undefined;
      const hasOnlyClipsSelected = hasOnlyClipsSelectedSelector(store.getState());
      const hasOnlyBoardsSelected = hasOnlyBoardsSelectedSelector(store.getState());
      const selectedCount = selectedItemsCountSelector(store.getState());
      const selectedClipsInfo = selectedClipsIdsInfoSelector(store.getState());
      const selectedClipAssetIds = selectedClipsInfo.map(({ assetId }) => assetId);
      const selectedClipIds = selectedClipsInfo.map(({ id }) => id);
      const selectedBoards = selectedBoardsSelector(store.getState());
      const selectedBoardsIds = selectedBoardsIdsSelector(store.getState());
      const canDeleteAllAssets =
        selectedClipIds.length > 0
          ? canDeleteAsset(currentBoardId ? currentBoardPermissions : workspacePermissions)
          : true;
      const canDeleteAllBoards =
        selectedBoards.length > 0
          ? selectedBoards.every((board) => canDeleteBoard(getBoardContainerPermissions({ board })))
          : true;

      if (!canDeleteAllAssets || !canDeleteAllBoards) {
        return;
      }

      if (hasOnlyClipsSelected) {
        showDeletePrivateClipsModal({
          assetIds: selectedClipAssetIds,
          trackLocation,
          parentBoardId: currentBoardId,
          onDelete: () => dispatch(resetSelectedItemsAction()),
        });
      } else if (hasOnlyBoardsSelected) {
        showDeleteBoardsModal({
          boardIds: selectedBoardsIds,
          onDelete: () => {
            dispatch(resetSelectedItemsAction());
          },
          trackLocation,
        });
      } else if (selectedCount > 0) {
        showDeleteAssetsAndBoardsModal({
          assetIds: selectedClipAssetIds,
          boardIds: selectedBoardsIds,
          onDelete: () => dispatch(resetSelectedItemsAction()),
          trackLocation,
          parentBoardId: currentBoardId,
        });
      }
    },
    [
      account,
      dispatch,
      getBoardContainerPermissions,
      getBoardPermissions,
      showDeleteAssetsAndBoardsModal,
      showDeleteBoardsModal,
      showDeleteCanvasConfirmationModal,
      showDeletePrivateClipsModal,
      store,
      workspacePermissions,
    ],
  );

  return handleDelete;
};
